// © Microsoft Corporation. All rights reserved.

import { ObjectTelemetryContract, TelemetryAttrName, TelemetryMetadataAttribute, TelemetryObjectBase, TrackingOptions } from "@msnews/telemetry-contracts";

import { TelemetryContractCoverter } from "./TelemetryContractCoverter";

/**
 * Object metadata class which combination of external contract and internal data
 * @class TelemetryObject
 */
export class TelemetryObject implements TelemetryObjectBase {
    // #region Public properties
    /**
     * External object contract.
     */
    public contract: ObjectTelemetryContract;
    // #endregion

    constructor(contract: ObjectTelemetryContract, parent?: TelemetryObject, isRoot?: boolean) {
        this.contract = contract;
    }

    /**
     * Render telemetry attribute.
     * @param contract Object contract.
     * @returns Telemetry attribute.
     */
    public static render(contract: ObjectTelemetryContract): any {
        const attr = TelemetryContractCoverter.compress(contract);
        return { [TelemetryAttrName]: JSON.stringify(attr) };
    }

    // #region Public functions
    /**
     * Add a new telemetry object as a child.
     * @param contract Object telemetry contract.
     * @returns New telemetry object.
     */
    public addChild(contract: ObjectTelemetryContract): TelemetryObject {
        return new TelemetryObject(contract, this);
    }

    /**
     * Add or update a child telemetry object.
     * @param contract Object telemetry contract.
     * @param savedChildObject Existing child telemetry object (if exist).
     * @returns New telemetry object.
     */
    public addOrUpdateChild(contract: ObjectTelemetryContract, savedChildObject?: TelemetryObject): TelemetryObject {
        if (savedChildObject) {
            savedChildObject.contract = contract;
            return savedChildObject;
        } else {
            return new TelemetryObject(contract, this);
        }
    }

    /**
     * Update telemetry contract.
     * @param contract Object contract.
     */
    public updateContract(contract: ObjectTelemetryContract): void {
        this.contract = contract;
    }

    /**
     * Render telemetry attribute.
     * @param option Tracking options.
     * @returns Telemetry attribute.
     */
    public render(option?: TrackingOptions): any {
        return TelemetryObject.render(this.contract);
    }

    /**
     * Produces a string version of the telemetry object's metadata
     * @returns Stringified json telemetry object
     */
    public getMetadataTag(): string {
        return JSON.stringify(this.getMetadata());
    }

    /**
     * Produces a string version of the telemetry object's metadata
     * @returns Stringified json telemetry object
     */
    public getMetadata(): TelemetryMetadataAttribute {
        return TelemetryContractCoverter.compress(this.contract);
    }
    // #endregion
}
